import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, User } from "lucide-react";

const empty = { name: "", description: "", traits: [], appearance: "", gender: "", voice_id: "", portrait_url: "" };

const CharacterForm = ({ initial, onSave, isAdmin, allowPredefined, lipsyncEnabled }) => {
  const [form, setForm] = useState(initial || empty);
  const [traitsText, setTraitsText] = useState((initial?.traits || []).join(", "));
  const [isPredef, setIsPredef] = useState(initial?.is_predefined || false);

  const save = () => {
    if (!form.name.trim() || !form.description.trim()) {
      toast.error("Name and description are required");
      return;
    }
    onSave({
      ...form,
      voice_id: (form.voice_id || "").trim(),
      portrait_url: (form.portrait_url || "").trim(),
      traits: traitsText.split(",").map(t => t.trim()).filter(Boolean),
      is_predefined: allowPredefined && isAdmin ? isPredef : false,
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Name</Label>
        <Input data-testid="char-form-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
          className="mt-2 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading" />
      </div>
      <div>
        <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Gender (free text)</Label>
        <Input data-testid="char-form-gender" value={form.gender || ""} onChange={(e) => setForm({...form, gender: e.target.value})}
          className="mt-2 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading"
          placeholder="female · male · non-binary · anything" />
      </div>
      <div>
        <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Description</Label>
        <Textarea data-testid="char-form-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
          className="mt-2 bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading min-h-[100px]"
          placeholder="Who are they, what do they want, what do they hide?" />
      </div>
      <div>
        <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Traits (comma separated)</Label>
        <Input data-testid="char-form-traits" value={traitsText} onChange={(e) => setTraitsText(e.target.value)}
          className="mt-2 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading"
          placeholder="curious, reserved, dangerous" />
      </div>
      <div>
        <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Appearance</Label>
        <Textarea data-testid="char-form-appearance" value={form.appearance || ""} onChange={(e) => setForm({...form, appearance: e.target.value})}
          className="mt-2 bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading min-h-[80px]"
          placeholder="Hair, carriage, a detail you can picture" />
      </div>
      {lipsyncEnabled && (
        <>
          <div className="pt-3 border-t border-stone-800/60">
            <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-3">
              Voice & Portrait <span className="text-stone-600 normal-case tracking-normal italic">— optional</span>
            </div>
            <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">ElevenLabs Voice ID</Label>
            <Input
              data-testid="char-form-voice-id"
              value={form.voice_id || ""}
              onChange={(e) => setForm({...form, voice_id: e.target.value})}
              className="mt-2 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading"
              placeholder="e.g. yov4GXuuJIiSaVOg0Aij"
            />
            <p className="text-[10px] font-sans-ui text-stone-500 mt-1.5 leading-relaxed">
              From your ElevenLabs VoiceLab. Required for the ▶ play-voice button on this character&apos;s lines.
            </p>
          </div>
          <div>
            <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Portrait URL</Label>
            <Input
              data-testid="char-form-portrait-url"
              value={form.portrait_url || ""}
              onChange={(e) => setForm({...form, portrait_url: e.target.value})}
              className="mt-2 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading"
              placeholder="https://… (square headshot, 512×512+)"
            />
            <p className="text-[10px] font-sans-ui text-stone-500 mt-1.5 leading-relaxed">
              Used by Face-to-Face Mode (coming soon). Square headshot recommended.
            </p>
          </div>
        </>
      )}
      {allowPredefined && isAdmin && (
        <label className="flex items-center gap-3 font-sans-ui text-xs uppercase tracking-[0.2em] text-stone-400 cursor-pointer">
          <input type="checkbox" checked={isPredef} onChange={(e) => setIsPredef(e.target.checked)} data-testid="char-form-predefined" />
          Mark as predefined (visible to all users)
        </label>
      )}
      <DialogFooter>
        <Button data-testid="char-form-save" onClick={save} className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-11 font-sans-ui text-xs uppercase tracking-[0.25em]">
          Save character
        </Button>
      </DialogFooter>
    </div>
  );
};

const Characters = () => {
  const { user } = useAuth();
  const [chars, setChars] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/characters");
      setChars(r.data);
    } catch { toast.error("Load failed"); }
  };

  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing) {
        await api.put(`/characters/${editing.id}`, data);
        toast.success("Character updated");
      } else {
        await api.post("/characters", data);
        toast.success("Character added");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (c) => {
    if (!confirm(`Delete ${c.name}?`)) return;
    try {
      await api.delete(`/characters/${c.id}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const predefined = chars.filter(c => c.is_predefined);
  const mine = chars.filter(c => !c.is_predefined);

  return (
    <div className="min-h-screen pt-24 pb-20 bg-stone-950 grain-overlay" data-testid="characters-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div>
            <div className="overline text-xs font-sans-ui uppercase tracking-[0.3em] text-stone-500 mb-3">
              Dramatis personae
            </div>
            <h1 className="font-serif-heading text-5xl md:text-6xl font-light text-stone-100 leading-none">
              Characters
            </h1>
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-character-btn" onClick={() => setEditing(null)}
                className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-12 px-6 font-sans-ui text-xs uppercase tracking-[0.25em]">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New character
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-stone-950 border-stone-800 rounded-none max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif-heading text-3xl font-light text-stone-100">
                  {editing ? "Refine" : "Compose"} a character
                </DialogTitle>
              </DialogHeader>
              <CharacterForm initial={editing} onSave={save} isAdmin={user?.role === "admin"} allowPredefined={true} lipsyncEnabled={!!user?.lipsync_enabled} />
            </DialogContent>
          </Dialog>
        </div>

        {mine.length > 0 && (
          <section className="mb-20">
            <div className="divider-ornament mb-10 text-xs">&#10042;&nbsp;&nbsp;YOUR LIBRARY&nbsp;&nbsp;&#10042;</div>
            <div className="grid md:grid-cols-3 gap-6">
              {mine.map((c) => (
                <CharCard key={c.id} c={c} onEdit={(x) => { setEditing(x); setOpen(true); }} onDelete={del} mine={true} />
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="divider-ornament mb-10 text-xs">&#10042;&nbsp;&nbsp;THE HOUSE CAST&nbsp;&nbsp;&#10042;</div>
          <div className="grid md:grid-cols-3 gap-6">
            {predefined.map((c) => (
              <CharCard key={c.id} c={c} mine={false} canEdit={user?.role === "admin"} canDelete={true}
                onEdit={(x) => { setEditing(x); setOpen(true); }} onDelete={del} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const CharCard = ({ c, onEdit, onDelete, mine, canEdit = true, canDelete = true }) => (
  <div data-testid={`char-card-${c.id}`} className="border border-stone-800 bg-stone-900/30 p-6 hover:border-stone-600 transition">
    <div className="flex items-start justify-between mb-3">
      <User className="w-4 h-4 text-rose-800/80 mt-1" strokeWidth={1.5} />
      <div className="flex gap-2">
        {(mine || canEdit) && (
          <button data-testid={`char-edit-${c.id}`} onClick={() => onEdit(c)} className="text-stone-500 hover:text-stone-200 transition">
            <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        )}
        {(mine || canDelete) && (
          <button data-testid={`char-delete-${c.id}`} onClick={() => onDelete(c)} className="text-stone-500 hover:text-rose-700 transition">
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
    <h3 className="font-serif-heading text-2xl text-stone-100 mb-2">{c.name}</h3>
    {c.gender && <div className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-3">{c.gender}</div>}
    <p className="font-serif-reading text-stone-400 text-sm italic leading-relaxed mb-4">{c.description}</p>
    <div className="flex flex-wrap gap-2">
      {c.traits?.slice(0,4).map((t,i) => (
        <span key={i} className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500 border border-stone-800 px-2 py-1">{t}</span>
      ))}
    </div>
  </div>
);

export default Characters;
